import _ from 'underscore';
import BaseExporter from './BaseExporter';
import ti18n from 'i18n!nls/ExportPopupi18n';

/**
 * SesxExporter - Export timeline data to Adobe Audition SESX format
 *
 * Features:
 * - SESX 1.9 format (Adobe Audition compatible)
 * - Sample-accurate timing at 48000 Hz
 * - Multi-track audio organization
 * - Video track support with audio extraction
 * - Professional audio post-production workflow
 *
 * Note: This is a simplified SESX export focusing on timeline structure.
 * Advanced features like effects chains, automation, and DSP can be added in future versions.
 */
const SesxExporter = BaseExporter.extend({

    /**
     * Export timeline data as SESX format
     * @param {Array} timelineData - Array of timeline clip objects
     * @param {Object} options - Export options
     * @returns {String} SESX content as XML
     */
    export: function(timelineData, options) {
        console.log('🎵 SESX EXPORT STARTED: SesxExporter.export() called with', timelineData.length, 'clips');

        options = this.validateAndExtendOptions(options);
        this.validateTimelineData(timelineData);

        try {
            const sampleRate = options.sampleRate || 48000;
            const bitDepth = options.bitDepth || 24;
            const totalDuration = this.getTotalDuration(timelineData);
            const totalSamples = this._convertSecondsToSamples(totalDuration, sampleRate);

            // Generate SESX XML structure
            let sesxContent = `<?xml version="1.0" encoding="UTF-8" standalone="no" ?>\n`;
            sesxContent += `<!DOCTYPE sesx>\n`;
            sesxContent += `<sesx version="1.9">\n\n`;

            // Session header
            sesxContent += this._generateSessionHeader(totalSamples, sampleRate, bitDepth, options);

            // Tracks section
            sesxContent += `    <tracks>\n`;
            sesxContent += this._generateTracks(timelineData, sampleRate, options);
            sesxContent += `    </tracks>\n`;

            // Session state
            sesxContent += this._generateSessionState(totalSamples, options);

            sesxContent += `  </session>\n\n`;

            // Files section
            sesxContent += this._generateFilesSection(timelineData, options);

            // Audio device (basic configuration)
            sesxContent += this._generateAudioDevice();

            sesxContent += `</sesx>\n`;

            console.log('🎵 SESX EXPORT: Successfully generated SESX content');
            return sesxContent;

        } catch (error) {
            console.error('Error generating SESX:', error);
            throw this.createExportError(window.getI18n(ti18n, 'EXPORT_FAILED'), error);
        }
    },

    /**
     * Generate session header with global settings
     * @private
     * @param {Number} totalSamples - Total duration in samples
     * @param {Number} sampleRate - Sample rate (48000 Hz default)
     * @param {Number} bitDepth - Bit depth (24-bit default)
     * @param {Object} options - Export options
     * @returns {String} Session header XML
     */
    _generateSessionHeader: function(totalSamples, sampleRate, bitDepth, options) {
        const audioChannelType = options.audioChannelType || 'stereo';
        const appVersion = '25.3'; // Adobe Audition version
        const appBuild = '25.3.0.71';

        let header = `  <session appBuild="${appBuild}" appVersion="${appVersion}" `;
        header += `audioChannelType="${audioChannelType}" `;
        header += `bitDepth="${bitDepth}" `;
        header += `duration="${totalSamples}" `;
        header += `sampleRate="${sampleRate}">\n`;

        return header;
    },

    /**
     * Generate tracks section with video and audio tracks
     * @private
     * @param {Array} timelineData - Timeline clip data
     * @param {Number} sampleRate - Sample rate
     * @param {Object} options - Export options
     * @returns {String} Tracks XML content
     */
    _generateTracks: function(timelineData, sampleRate, options) {
        let tracksXml = '';

        // Build file ID mapping (must match _generateFilesSection logic)
        const fileIdMap = new Map();
        let fileId = 0;
        timelineData.forEach(clip => {
            if (clip.fileName && !fileIdMap.has(clip.fileName)) {
                fileIdMap.set(clip.fileName, fileId);
                fileId++;
            }
        });

        // Group clips by source file
        const clipsBySource = new Map();
        timelineData.forEach(clip => {
            if (!clipsBySource.has(clip.fileName)) {
                clipsBySource.set(clip.fileName, []);
            }
            clipsBySource.get(clip.fileName).push(clip);
        });

        let trackId = 0;
        let audioTrackIndex = 1;

        // Generate video track (first video source only)
        const videoSources = Array.from(clipsBySource.entries()).filter(([_, clips]) =>
            clips.some(c => c.hasVideo)
        );

        if (videoSources.length > 0) {
            const [videoFileName, videoClips] = videoSources[0];
            tracksXml += this._generateVideoTrack(trackId, videoClips, fileIdMap, sampleRate, options);
            trackId++;
        }

        // Calculate master track ID (will be after all audio tracks)
        const audioTrackCount = clipsBySource.size;
        const masterTrackId = trackId + audioTrackCount;

        // Generate audio tracks (one per source file)
        Array.from(clipsBySource.entries()).forEach(([fileName, clips]) => {
            const trackName = `A${audioTrackIndex}`;
            // Determine track channel type based on clips from this source
            // Use the channel count from the first clip of this source
            const trackChannels = clips[0].channels || 2; // Default to stereo
            console.log(`🎵 SESX Track Generation - fileName: ${fileName}, trackChannels from clip: ${clips[0].channels}, using: ${trackChannels}`);
            tracksXml += this._generateAudioTrack(trackId, audioTrackIndex, trackName, clips, fileIdMap, masterTrackId, trackChannels, sampleRate, options);
            trackId++;
            audioTrackIndex++;
        });

        // Generate master track
        tracksXml += this._generateMasterTrack(masterTrackId);

        return tracksXml;
    },

    /**
     * Generate video track with video clips
     * @private
     * @param {Number} trackId - Track ID
     * @param {Array} clips - Video clips for this track
     * @param {Map} fileIdMap - Map of fileName to file ID
     * @param {Number} sampleRate - Sample rate
     * @param {Object} options - Export options
     * @returns {String} Video track XML
     */
    _generateVideoTrack: function(trackId, clips, fileIdMap, sampleRate, options) {
        let xml = `      <videoTrack id="${trackId}" select="false">\n`;
        xml += `        <trackParameters trackHeight="70" trackHue="-1" trackMinimized="false">\n`;
        xml += `          <name>V1</name>\n`;
        xml += `        </trackParameters>\n`;

        // Generate video clips
        clips.forEach((clip, index) => {
            if (clip.hasVideo) {
                const fileID = fileIdMap.get(clip.fileName);
                xml += this._generateVideoClip(index, fileID, clip, sampleRate, options);
            }
        });

        xml += `      </videoTrack>\n`;
        return xml;
    },

    /**
     * Generate audio track with audio clips
     * @private
     * @param {Number} trackId - Track ID
     * @param {Number} trackIndex - Track index for naming
     * @param {String} trackName - Track name
     * @param {Array} clips - Audio clips for this track
     * @param {Map} fileIdMap - Map of fileName to file ID
     * @param {Number} masterTrackId - Master track ID for routing
     * @param {Number} trackChannels - Number of channels for this track (1=mono, 2=stereo)
     * @param {Number} sampleRate - Sample rate
     * @param {Object} options - Export options
     * @returns {String} Audio track XML
     */
    _generateAudioTrack: function(trackId, trackIndex, trackName, clips, fileIdMap, masterTrackId, trackChannels, sampleRate, options) {
        let xml = `      <audioTrack automationLaneOpenState="false" id="${trackId}" index="${trackIndex}" select="false" visible="true">\n`;
        xml += `        <trackParameters trackHeight="156" trackHue="-1" trackMinimized="false">\n`;
        xml += `          <name>${this.escapeXml(trackName)}</name>\n`;
        xml += `        </trackParameters>\n`;

        // Track audio parameters (volume, pan, etc.) - route to master
        xml += this._generateTrackAudioParameters(trackId, false, masterTrackId, trackChannels);

        // Generate audio clips
        clips.forEach((clip, index) => {
            const fileID = fileIdMap.get(clip.fileName);
            xml += this._generateAudioClip(index, fileID, clip, sampleRate, options);
        });

        xml += `      </audioTrack>\n`;
        return xml;
    },

    /**
     * Generate master track (final output bus)
     * @private
     * @param {Number} trackId - Track ID
     * @returns {String} Master track XML
     */
    _generateMasterTrack: function(trackId) {
        let xml = `      <masterTrack automationLaneOpenState="false" id="${trackId}" index="${trackId}" select="true" visible="true">\n`;
        xml += `        <trackParameters trackHeight="156" trackHue="-1" trackMinimized="false">\n`;
        xml += `          <name>Master</name>\n`;
        xml += `        </trackParameters>\n`;

        // Master track audio parameters
        xml += this._generateTrackAudioParameters(trackId, true);

        xml += `      </masterTrack>\n`;
        return xml;
    },

    /**
     * Generate track audio parameters (volume, pan, EQ, etc.)
     * @private
     * @param {Number} trackId - Track ID
     * @param {Boolean} isMaster - Whether this is the master track
     * @param {Number} masterTrackId - Master track ID for routing (optional)
     * @param {Number} trackChannels - Number of channels for this track (1=mono, 2=stereo)
     * @returns {String} Track audio parameters XML
     */
    _generateTrackAudioParameters: function(trackId, isMaster = false, masterTrackId = null, trackChannels = 2) {
        // Determine channel type based on actual track channels
        // Master is always stereo, audio tracks match their source files
        const channelType = isMaster ? 'stereo' : (trackChannels === 1 ? 'mono' : 'stereo');
        const outputID = isMaster ? '1' : String(masterTrackId);
        const outputType = isMaster ? 'hardwareOutput' : 'trackID';

        // Debug logging
        console.log(`🎵 SESX Track Audio Parameters - trackId: ${trackId}, isMaster: ${isMaster}, trackChannels: ${trackChannels}, channelType: ${channelType}`);

        // Use correct input port: mono tracks use id="2", stereo tracks use id="1"
        const inputID = channelType === 'mono' ? '2' : '1';

        let xml = `        <trackAudioParameters audioChannelType="${channelType}" automationMode="1" monitoring="false" recordArmed="false" solo="false" soloSafe="false">\n`;
        xml += `          <trackOutput outputID="${outputID}" type="${outputType}"/>\n`;
        xml += `          <trackInput inputID="${inputID}"/>\n`;

        // Basic track fader component
        xml += `          <component componentGuid="${this._generateGuid()}" componentID="Audition.Fader" id="trackFader" name="volume" powered="true">\n`;
        xml += `            <parameter index="0" name="volume" parameterValue="1"/>\n`;
        xml += `            <parameter index="1" name="static gain" parameterValue="1"/>\n`;
        xml += `          </component>\n`;

        // Mute component
        xml += `          <component componentGuid="${this._generateGuid()}" componentID="Audition.Mute" id="trackMute" name="Mute" powered="true">\n`;
        xml += `            <parameter index="0" parameterValue="0"/>\n`;
        xml += `            <parameter index="1" name="mute" parameterValue="0"/>\n`;
        xml += `          </component>\n`;

        // Pan component - use appropriate panner based on channel type
        if (channelType === 'mono') {
            xml += `          <component componentGuid="${this._generateGuid()}" componentID="Audition.MonoPanner" id="trackPan" name="MonoPanner" powered="true">\n`;
            xml += `            <parameter index="0" name="Pan" parameterValue="0"/>\n`;
            xml += `          </component>\n`;
        } else {
            xml += `          <component componentGuid="${this._generateGuid()}" componentID="Audition.StereoPanner" id="trackPan" name="StereoPanner" powered="true">\n`;
            xml += `            <parameter index="0" name="Pan" parameterValue="0"/>\n`;
            xml += `          </component>\n`;
        }

        // EQ component (powered off by default)
        xml += `          <component componentGuid="${this._generateGuid()}" componentID="Audition.EQ" id="trackEQ" name="EQ" powered="false">\n`;
        xml += `            <parameter index="0" name="Low Shelf Frequency" parameterValue="0.000834028352983295917510986328125"/>\n`;
        xml += `            <parameter index="1" name="Low Shelf Gain" parameterValue="0.5"/>\n`;
        xml += `          </component>\n`;

        xml += `        </trackAudioParameters>\n`;
        xml += `        <editParameter parameterIndex="0" slotIndex="4294967280"/>\n`;

        return xml;
    },

    /**
     * Generate video clip XML
     * @private
     * @param {Number} clipId - Clip ID
     * @param {Number} fileID - File ID reference
     * @param {Object} clip - Clip data
     * @param {Number} sampleRate - Sample rate
     * @param {Object} options - Export options
     * @returns {String} Video clip XML
     */
    _generateVideoClip: function(clipId, fileID, clip, sampleRate, options) {
        const startSamples = this._convertSecondsToSamples(clip.startTimeInSeconds, sampleRate);
        const endSamples = this._convertSecondsToSamples(clip.endTimeInSeconds, sampleRate);
        const sourceInSamples = this._convertSecondsToSamples(clip.sourceStartInSeconds, sampleRate);
        const sourceOutSamples = this._convertSecondsToSamples(clip.sourceStartInSeconds + clip.durationInSeconds, sampleRate);

        let xml = `        <videoClip endPoint="${endSamples}" fileID="${fileID}" hue="153" id="${clipId}" `;
        xml += `lockedInTime="false" name="${this.escapeXml(clip.fileName)}" offline="false" select="false" `;
        xml += `sourceInPoint="${sourceInSamples}" sourceOutPoint="${sourceOutSamples}" startPoint="${startSamples}" zOrder="0">\n`;

        // Video format information
        xml += `          <videoFormat fieldType="0" height="1080" parDenominator="1" parNumerator="1" `;
        xml += `pixelType="0" sampleSize="8467191533" timeScale="254016000000" width="1920"/>\n`;

        xml += `        </videoClip>\n`;

        return xml;
    },

    /**
     * Generate audio clip XML
     * @private
     * @param {Number} clipId - Clip ID
     * @param {Number} fileID - File ID reference
     * @param {Object} clip - Clip data
     * @param {Number} sampleRate - Sample rate
     * @param {Object} options - Export options
     * @returns {String} Audio clip XML
     */
    _generateAudioClip: function(clipId, fileID, clip, sampleRate, options) {
        const startSamples = this._convertSecondsToSamples(clip.startTimeInSeconds, sampleRate);
        const endSamples = this._convertSecondsToSamples(clip.endTimeInSeconds, sampleRate);
        const sourceInSamples = this._convertSecondsToSamples(clip.sourceStartInSeconds, sampleRate);
        const sourceOutSamples = this._convertSecondsToSamples(clip.sourceStartInSeconds + clip.durationInSeconds, sampleRate);

        let xml = `        <audioClip clipAutoCrossfade="true" crossFadeHeadClipID="-1" crossFadeTailClipID="-1" `;
        xml += `endPoint="${endSamples}" fileID="${fileID}" hue="153" id="${clipId}" lockedInTime="false" looped="false" `;
        xml += `name="${this.escapeXml(clip.fileName)}" offline="false" select="false" `;
        xml += `sourceInPoint="${sourceInSamples}" sourceOutPoint="${sourceOutSamples}" startPoint="${startSamples}" zOrder="0">\n`;

        // Effects rack (empty for now)
        xml += this._generateEffectsRack();

        // Clip gain component
        xml += `          <component componentGuid="${this._generateGuid()}" componentID="Audition.Fader" id="clipGain" name="volume" powered="true">\n`;
        xml += `            <parameter index="0" name="volume" parameterValue="1"/>\n`;
        xml += `            <parameter index="1" name="static gain" parameterValue="1"/>\n`;
        xml += `          </component>\n`;

        // Clip mute component
        xml += `          <component componentGuid="${this._generateGuid()}" componentID="Audition.Mute" id="clipMute" name="Mute" powered="true">\n`;
        xml += `            <parameter index="0" parameterValue="0"/>\n`;
        xml += `            <parameter index="1" name="mute" parameterValue="0"/>\n`;
        xml += `          </component>\n`;

        // Clip pan component
        xml += `          <component id="clipPan" powered="true"/>\n`;

        // Fade in/out (no fades by default)
        xml += `          <fadeIn crossFadeLinkType="linkedAsymmetric" endPoint="0" shape="19" startPoint="0" type="log"/>\n`;
        xml += `          <fadeOut crossFadeLinkType="linkedAsymmetric" endPoint="${endSamples}" shape="19" startPoint="${endSamples}" type="log"/>\n`;

        xml += `          <editParameter parameterIndex="0" slotIndex="4294967280"/>\n`;

        // Channel map - use actual channel count from clip data
        const clipChannels = clip.channels || 2; // Default to stereo if not specified
        xml += this._generateChannelMap(clipChannels);

        xml += `        </audioClip>\n`;

        return xml;
    },

    /**
     * Generate channel map for audio clip based on number of channels
     * @private
     * @param {Number} channels - Number of audio channels (1=mono, 2=stereo)
     * @returns {String} Channel map XML
     */
    _generateChannelMap: function(channels) {
        let xml = `          <channelMap>\n`;

        if (channels === 1) {
            // Mono: map source channel 0 to output channel 0
            xml += `            <channel index="0" sourceIndex="0"/>\n`;
        } else {
            // Stereo (or multi-channel): map each source channel to corresponding output
            // Most common case - stereo files with left and right channels
            for (let i = 0; i < channels; i++) {
                xml += `            <channel index="${i}" sourceIndex="${i}"/>\n`;
            }
        }

        xml += `          </channelMap>\n`;
        return xml;
    },

    /**
     * Generate empty effects rack for clips
     * @private
     * @returns {String} Effects rack XML
     */
    _generateEffectsRack: function() {
        let xml = `          <effectsRack preFader="false">\n`;
        xml += `            <component componentGuid="${this._generateGuid()}" componentID="Audition.ComponentGroup" id="rackMix" powered="true">\n`;
        xml += `              <parameter index="0" parameterValue="0"/>\n`;
        xml += `              <parameter index="1" parameterValue="0"/>\n`;
        xml += `            </component>\n`;
        xml += `            <component componentGuid="${this._generateGuid()}" id="rackInput" powered="true">\n`;
        xml += `              <parameter index="0" parameterValue="1"/>\n`;
        xml += `              <parameter index="1" parameterValue="0"/>\n`;
        xml += `            </component>\n`;
        xml += `            <component componentGuid="${this._generateGuid()}" id="rackOutput" powered="true">\n`;
        xml += `              <parameter index="0" parameterValue="1"/>\n`;
        xml += `              <parameter index="1" parameterValue="0"/>\n`;
        xml += `            </component>\n`;
        xml += `          </effectsRack>\n`;
        return xml;
    },

    /**
     * Generate session state (playhead position, selection, view settings)
     * @private
     * @param {Number} totalSamples - Total duration in samples
     * @param {Object} options - Export options
     * @returns {String} Session state XML
     */
    _generateSessionState: function(totalSamples, options) {
        const frameRate = options.frameRate || 25;

        let xml = `    <sessionState ctiPosition="0" smpteStart="0">\n`;
        xml += `      <selectionState selectionDuration="0" selectionStart="0"/>\n`;
        xml += `      <viewState horizontalViewDuration="${totalSamples}" horizontalViewStart="0" trackControlsWidth="224" verticalScrollOffset="0"/>\n`;
        xml += `      <timeFormatState beatsPerBar="4" beatsPerMinute="120" customFrameRate="${frameRate}" linkToDefaultTimeSettings="false" `;
        xml += `noteLength="4" subdivisions="16" timeCodeDropFrame="false" timeCodeFrameRate="${frameRate}" timeCodeNTSC="false" timeFormat="timeFormatTimeCode"/>\n`;
        xml += `      <mixingOptionState defaultPanModeLogarithmic="true" panPower="-3" playOverlappingClips="false"/>\n`;
        xml += `    </sessionState>\n`;

        return xml;
    },

    /**
     * Generate files section with media file references
     * @private
     * @param {Array} timelineData - Timeline clip data
     * @param {Object} options - Export options
     * @returns {String} Files section XML
     */
    _generateFilesSection: function(timelineData, options) {
        let xml = `  <files>\n`;

        // Get unique source files
        const uniqueFiles = new Map();
        let fileId = 0;

        timelineData.forEach(clip => {
            if (clip.fileName && !uniqueFiles.has(clip.fileName)) {
                const filePath = this.getMediaFilePath(clip, options);
                const relativePath = options.mediaPathType === 'relative'
                    ? `media/${clip.fileName}`
                    : clip.fileName;

                const mediaHandler = clip.hasVideo ? 'ImporterHost' : 'AmioWav';

                uniqueFiles.set(clip.fileName, {
                    id: fileId,
                    absolutePath: filePath,
                    relativePath: relativePath,
                    mediaHandler: mediaHandler
                });

                fileId++;
            }
        });

        // Generate file entries
        uniqueFiles.forEach(fileInfo => {
            xml += `    <file absolutePath="${this.escapeXml(fileInfo.absolutePath)}" `;
            xml += `id="${fileInfo.id}" `;
            xml += `mediaHandler="${fileInfo.mediaHandler}" `;
            xml += `relativePath="${this.escapeXml(fileInfo.relativePath)}"/>\n`;
        });

        xml += `  </files>\n\n`;
        return xml;
    },

    /**
     * Generate audio device section (basic configuration)
     * @private
     * @returns {String} Audio device XML
     */
    _generateAudioDevice: function() {
        let xml = `  <audioDevice inputID="22222222-2222-2222-2222-22222222" outputID="11111111-1111-1111-1111-11111111">\n`;
        xml += `    <inputPort id="1" name="Default Stereo Input"/>\n`;
        xml += `    <inputPort id="2" name="Default Mono Input"/>\n`;
        xml += `    <outputPort id="1" name="Default Output"/>\n`;
        xml += `    <outputPort id="4" name="Default Stereo Output"/>\n`;
        xml += `  </audioDevice>\n\n`;
        return xml;
    },

    /**
     * Convert seconds to samples at given sample rate
     * @private
     * @param {Number} seconds - Time in seconds
     * @param {Number} sampleRate - Sample rate (Hz)
     * @returns {Number} Time in samples (rounded)
     */
    _convertSecondsToSamples: function(seconds, sampleRate) {
        return Math.round(seconds * sampleRate);
    },

    /**
     * Generate a pseudo-random GUID for Adobe Audition components
     * @private
     * @returns {String} GUID in format xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
     */
    _generateGuid: function() {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    },

    /**
     * Get SESX-specific default options
     * @returns {Object} SESX format defaults
     */
    getDefaultOptions: function() {
        return _.extend(BaseExporter.prototype.getDefaultOptions.call(this), {
            sampleRate: 48000,
            bitDepth: 24,
            audioChannelType: 'stereo',
            clipChannels: 2,                    // Default to stereo clips (safer for compatibility)
            projectTitle: 'Trebble SESX Project',
            mediaPathType: 'url' // SESX works well with absolute paths
        });
    },

    /**
     * Get supported SESX features
     * @returns {Object} Supported features and capabilities
     */
    getSupportedFeatures: function() {
        return {
            version: '1.9',
            supports: {
                video: true,
                audio: true,
                multiTrack: true,
                sampleAccurate: true,
                effects: false,      // Basic export for now
                automation: false,   // Can be added later
                routing: true
            },
            sampleRates: [44100, 48000, 96000],
            bitDepths: [16, 24, 32],
            audioChannelTypes: ['mono', 'stereo', '5.1']
        };
    },

    /**
     * Generate SESX project info
     * @param {Object} options - Export options (optional)
     * @returns {Object} Project information for SESX
     */
    getProjectInfo: function(options) {
        options = options || {};

        return {
            format: 'SESX',
            sampleRate: options.sampleRate || 48000,
            bitDepth: options.bitDepth || 24,
            version: '1.9',
            compatible: ['Adobe Audition CC', 'Adobe Audition 2023+'],
            features: [
                'Multi-track audio editing',
                'Sample-accurate timing',
                'Video track support',
                'Professional audio post-production'
            ]
        };
    }

});

export default SesxExporter;
